import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, ApiError } from "../lib/api";
import { useAuth, type AuthUser } from "../stores/auth";

export const Route = createFileRoute("/change-password")({
  component: ChangePasswordPage,
});

function ChangePasswordPage() {
  const nav = useNavigate();
  const queryClient = useQueryClient();
  const setUser = useAuth((s) => s.setUser);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await api<{ ok: true }>("/auth/password", {
        method: "POST",
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const res = await api<{ user: AuthUser }>("/auth/me");
      queryClient.setQueryData(["auth", "me"], res.user);
      setUser(res.user);
      await nav({ to: "/" });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "password change failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      className="mx-auto mt-[max(2rem,env(safe-area-inset-top))] w-[calc(100%-2rem)] max-w-sm space-y-4 rounded-md border border-border/60 p-5 sm:mt-24 sm:space-y-3 sm:p-6"
    >
      <h1 className="text-lg font-semibold">Change password</h1>
      <label className="block text-sm text-muted sm:text-xs">
        Current password
        <input
          type="password"
          name="currentPassword"
          autoComplete="current-password"
          required
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          className="mt-1 min-h-11 w-full rounded border border-border bg-transparent px-3 py-2 text-base sm:min-h-0 sm:px-2 sm:py-1.5 sm:text-sm"
        />
      </label>
      <label className="block text-sm text-muted sm:text-xs">
        New password
        <input
          type="password"
          name="newPassword"
          autoComplete="new-password"
          required
          minLength={10}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="mt-1 min-h-11 w-full rounded border border-border bg-transparent px-3 py-2 text-base sm:min-h-0 sm:px-2 sm:py-1.5 sm:text-sm"
        />
      </label>
      {err && <div role="alert" className="text-sm text-negRed sm:text-xs">{err}</div>}
      <button
        type="submit"
        disabled={busy}
        className="min-h-11 w-full rounded bg-fg px-3 py-2 text-base font-medium text-bg disabled:opacity-50 sm:min-h-0 sm:bg-accent sm:py-1.5 sm:text-sm sm:font-normal sm:text-white"
      >
        {busy ? "Saving..." : "Save password"}
      </button>
    </form>
  );
}
